import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Globe2, Clock, ExternalLink, ChevronRight, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { COVERAGE_LEVELS, MARKET_INTEL_CATEGORIES, SEARCH_GROUPS } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";

const coverageCls = { covered: "border-champagne bg-champagne/10 text-champagne", developing: "border-anthracite/30 bg-sand text-anthracite/70", on_demand: "border-tertiary bg-white text-anthracite/50" };
const RELATED_DEFS = SEARCH_GROUPS.filter((g) => ["opportunities", "projects", "investments", "partners", "experts", "sourcing"].includes(g.key));

export default function MarketDetail() {
  const { country, sector } = useParams();
  const { lang, L, t } = useLang();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setM(null); setError(false);
    const url = sector ? `/markets/${country}/${sector}` : `/markets/${country}`;
    api.get(url).then(({ data }) => setM(data)).catch(() => setError(true));
  }, [country, sector]);
  useSeo({ title: m ? (L(m, "country_name") || m.country) : "", description: m ? L(m, "summary") : "", lang, image: m?.cover_image, type: "article" });

  if (error) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/60">404 — {lang === "fr" ? "aucun contenu disponible" : "no content available"}</div>;
  if (!m) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/40">…</div>;
  const v = m.verification || {};
  const cov = COVERAGE_LEVELS.find((c) => c.value === m.coverage_level);
  const catLabel = (k) => MARKET_INTEL_CATEGORIES.find((x) => x.value === k)?.[lang] || k;
  const related = m.related || {};

  const S = ({ title, body }) => body ? (<div className="mb-8"><h3 className="mb-2 font-serif text-xl text-anthracite">{title}</h3><p className="whitespace-pre-line text-base leading-relaxed text-anthracite/80">{body}</p></div>) : null;

  const itemTitle = (g, it) => {
    if (g.key === "partners") return it.organization;
    if (g.key === "experts") return it.name;
    return L(it, "title") || L(it, "product") || it.title_fr || "—";
  };

  return (
    <article className="mx-auto max-w-4xl px-6 py-12 lg:px-10">
      <button onClick={() => navigate(sector ? `/marches/${country}` : "/marches")} className="mb-8 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne"><ArrowLeft className="h-4 w-4" /> {sector ? (L(m, "country_name") || m.country) : (lang === "fr" ? "Tous les marchés" : "All markets")}</button>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-anthracite/40"><Globe2 className="h-4 w-4" />{m.region || m.zone}</span>
        {cov && <span className={`rounded-full border px-3 py-1 text-xs ${coverageCls[m.coverage_level]}`}>{cov[lang]}</span>}
      </div>
      <h1 className="mb-2 font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{L(m, "country_name") || m.country}{sector ? ` · ${sector}` : ""}</h1>
      {cov && <p className="mb-6 text-sm text-anthracite/50">{lang === "fr" ? cov.descFr : cov.descEn}</p>}
      {L(m, "summary") && <p className="mb-8 text-lg leading-relaxed text-anthracite/70">{L(m, "summary")}</p>}
      {m.cover_image && <div className="mb-10 aspect-[16/9] overflow-hidden bg-sand"><img src={m.cover_image} alt="" className="h-full w-full object-cover" /></div>}

      {(m.priority_sectors || []).length > 0 && (
        <div className="mb-8">
          <p className={`mb-2 ${"text-xs uppercase tracking-[0.12em] text-anthracite/50"}`}>{lang === "fr" ? "Secteurs prioritaires" : "Priority sectors"}</p>
          <div className="flex flex-wrap gap-2">{m.priority_sectors.map((s) => <Link key={s} to={`/marches/${m.slug}/${s}`} className="rounded-full border border-champagne/30 bg-champagne/10 px-3 py-1 text-xs text-champagne hover:bg-champagne/20">{s}</Link>)}</div>
        </div>
      )}

      <S title={lang === "fr" ? "Analyse RSTI" : "RSTI analysis"} body={L(m, "rst_analysis")} />

      {(m.intelligence || []).length > 0 && (
        <div className="mb-10" data-testid="market-intelligence">
          <h3 className="mb-4 font-serif text-2xl text-anthracite">{lang === "fr" ? "Intelligence marché" : "Market intelligence"}</h3>
          <div className="space-y-4">
            {m.intelligence.map((b, i) => (L(b, "body") || L(b, "title")) ? (
              <div key={i} className="border border-tertiary bg-white p-5" data-testid={`intel-block-${i}`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-anthracite px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-ivory">{catLabel(b.category)}</span>
                  {L(b, "title") && <span className="font-medium text-anthracite">{L(b, "title")}</span>}
                </div>
                {L(b, "body") && <p className="whitespace-pre-line text-sm leading-relaxed text-anthracite/80">{L(b, "body")}</p>}
                {(b.source_name || b.source_url || b.verified_at) && (
                  <p className="mt-3 flex flex-wrap items-center gap-3 border-t border-tertiary pt-3 text-xs text-anthracite/50">
                    {b.source_name && <span>{t("source")}: {b.source_name}</span>}
                    {b.source_url && <a href={b.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-champagne hover:underline">{lang === "fr" ? "lien" : "link"}<ExternalLink className="h-3 w-3" /></a>}
                    {b.verified_at && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-champagne" />{new Date(b.verified_at).toLocaleDateString(lang)}</span>}
                  </p>
                )}
              </div>
            ) : null)}
          </div>
        </div>
      )}

      <S title={lang === "fr" ? "Informations pratiques" : "Practical information"} body={L(m, "practical_info")} />

      {/* Dynamic linked content */}
      {RELATED_DEFS.map((g) => {
        const list = related[g.key] || [];
        if (list.length === 0) return null;
        return (
          <div key={g.key} className="mb-8" data-testid={`related-${g.key}`}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-serif text-xl text-anthracite">{g[lang]}</h3>
              <Link to={g.path} className="text-xs text-champagne hover:underline">{lang === "fr" ? "Tout voir" : "See all"}</Link>
            </div>
            <div className="divide-y divide-tertiary border border-tertiary bg-white">
              {list.map((it) => (
                <Link key={it.id} to={`${g.path}/${it.slug}`} className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-sand/50">
                  <span className="text-anthracite">{itemTitle(g, it)}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-anthracite/30" />
                </Link>
              ))}
            </div>
          </div>
        );
      })}

      <div className="mb-6" data-testid="verification-block" />

      <p className="mb-10 text-xs text-anthracite/50">{lang === "fr" ? "RSTI n'a pas le même niveau d'expertise sur tous les pays. Le niveau de couverture indique la profondeur de la connaissance et des relations vérifiées, sans constituer une garantie." : "RSTI does not have the same expertise level in every country. The coverage level indicates the depth of verified knowledge and relationships, without being a guarantee."}</p>

      <div className="flex flex-col gap-3 border-t border-tertiary pt-8 sm:flex-row">
        <Link to={`/connect?category=markets&ref=${encodeURIComponent(L(m, "country_name") || m.country)}`} data-testid="cta-contact-rst" className="flex-1 bg-anthracite px-6 py-4 text-center text-sm font-medium text-ivory transition-colors hover:bg-black">{lang === "fr" ? "Contacter RSTI sur ce marché" : "Contact RSTI about this market"}</Link>
      </div>
    </article>
  );
}
